import { useId } from 'react';
import { t } from '../../lib/i18n';

// ---------- Capacity bar ----------

export type CapacityState = 'ok' | 'near' | 'full' | 'over';

/** How full a place is. `nearFraction` (default 0.9) is where "nearly full" starts; capacity 0 means unlimited. */
export function capacityState(current: number, capacity: number, nearFraction = 0.9): CapacityState {
  if (capacity <= 0) return 'ok';
  if (current > capacity) return 'over';
  if (current === capacity) return 'full';
  return current / capacity >= nearFraction ? 'near' : 'ok';
}

const BAR_COLOUR: Record<CapacityState, string> = { ok: 'bg-green-600', near: 'bg-amber-500', full: 'bg-red-600', over: 'bg-red-700' };

export interface CapacityBarProps {
  /** Name of what is measured, for example the site or area. */
  label: string;
  current: number;
  /** 0 = no limit set (shows the number only). */
  capacity: number;
  nearFraction?: number;
}

/** A horizontal meter with the numbers and the state in words (never colour alone). */
export function CapacityBar({ label, current, capacity, nearFraction }: CapacityBarProps) {
  const state = capacityState(current, capacity, nearFraction);
  const pct = capacity > 0 ? Math.min(100, Math.round((current / capacity) * 100)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium text-slate-800">{label}</span>
        <span className="tabular-nums text-slate-700">{capacity > 0 ? t('p3w.capacity.of', { current, capacity }) : t('p3w.capacity.noLimit', { current })}</span>
      </div>
      {capacity > 0 ? (
        <div
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={capacity}
          aria-valuenow={Math.min(current, capacity)}
          aria-valuetext={t('p3w.capacity.of', { current, capacity })}
          className="h-3 overflow-hidden rounded-full bg-slate-200"
        >
          <div className={`h-full rounded-full ${BAR_COLOUR[state]}`} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {capacity > 0 ? <p className="text-xs text-slate-600">{t(`p3w.capacity.state.${state}`)}</p> : null}
    </div>
  );
}

// ---------- Line chart ----------

export interface ChartPoint {
  /** Text shown on the x axis and in the data table (for example "09:00"). */
  x: string;
  y: number;
}
export interface ChartSeries {
  name: string;
  points: readonly ChartPoint[];
}

const SERIES_COLOURS = ['#2563eb', '#b45309', '#047857', '#7c3aed'];
const DASHES = ['', '6 4', '2 3', '8 3 2 3'];

/** A "nice" top for the y axis: 1, 2, 5 times a power of ten at or above the max. */
export function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 2, 5, 10]) if (m * pow >= max) return m * pow;
  return 10 * pow;
}

export interface LineChartProps {
  /** Title, used as the accessible name. */
  title: string;
  series: readonly ChartSeries[];
  height?: number;
  /** Fixed top of the y axis (for example the capacity). Default: a nice number above the data. */
  yMax?: number;
  /** Draws a dashed reference line (for example capacity) with this label. */
  limit?: { value: number; label: string };
}

/** A simple SVG line chart. Series differ by colour and dash pattern; a hidden table repeats the numbers for screen readers. */
export function LineChart({ title, series, height = 220, yMax, limit }: LineChartProps) {
  const id = useId();
  const W = 640;
  const pad = { l: 40, r: 12, t: 12, b: 28 };
  const labels = series[0]?.points.map((p) => p.x) ?? [];
  const n = labels.length;
  const top = yMax ?? niceMax(Math.max(0, limit?.value ?? 0, ...series.flatMap((s) => s.points.map((p) => p.y))));
  const iw = W - pad.l - pad.r;
  const ih = height - pad.t - pad.b;
  const px = (i: number) => pad.l + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const py = (v: number) => pad.t + ih - (Math.max(0, Math.min(top, v)) / top) * ih;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(top * f * 100) / 100);
  const labelEvery = Math.max(1, Math.ceil(n / 8));

  if (n === 0) return <p className="text-sm text-slate-600">{t('p3w.chart.noData')}</p>;
  return (
    <figure className="space-y-2">
      <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-labelledby={`${id}-t`} className="h-auto w-full">
        <title id={`${id}-t`}>{title}</title>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.l} x2={W - pad.r} y1={py(v)} y2={py(v)} stroke="#e2e8f0" />
            <text x={pad.l - 6} y={py(v) + 4} textAnchor="end" fontSize="11" fill="#475569">
              {v}
            </text>
          </g>
        ))}
        {labels.map((l, i) =>
          i % labelEvery === 0 ? (
            <text key={i} x={px(i)} y={height - 8} textAnchor="middle" fontSize="11" fill="#475569">
              {l}
            </text>
          ) : null,
        )}
        {limit ? (
          <g>
            <line x1={pad.l} x2={W - pad.r} y1={py(limit.value)} y2={py(limit.value)} stroke="#b91c1c" strokeDasharray="4 4" />
            <text x={W - pad.r} y={py(limit.value) - 4} textAnchor="end" fontSize="11" fill="#b91c1c">
              {limit.label}
            </text>
          </g>
        ) : null}
        {series.map((s, si) => (
          <g key={s.name}>
            <polyline
              fill="none"
              stroke={SERIES_COLOURS[si % SERIES_COLOURS.length]}
              strokeWidth="2.5"
              strokeDasharray={DASHES[si % DASHES.length]}
              points={s.points.map((p, i) => `${px(i)},${py(p.y)}`).join(' ')}
            />
            {s.points.map((p, i) => (
              <circle key={i} cx={px(i)} cy={py(p.y)} r="3" fill={SERIES_COLOURS[si % SERIES_COLOURS.length]} />
            ))}
          </g>
        ))}
      </svg>
      {series.length > 1 ? (
        <ul className="flex flex-wrap gap-4 text-xs text-slate-700" aria-label={t('p3w.chart.legend')}>
          {series.map((s, si) => (
            <li key={s.name} className="flex items-center gap-1.5">
              <svg width="24" height="8" aria-hidden="true">
                <line x1="0" x2="24" y1="4" y2="4" stroke={SERIES_COLOURS[si % SERIES_COLOURS.length]} strokeWidth="2.5" strokeDasharray={DASHES[si % DASHES.length]} />
              </svg>
              {s.name}
            </li>
          ))}
        </ul>
      ) : null}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th scope="col">{t('p3w.chart.x')}</th>
            {series.map((s) => (
              <th key={s.name} scope="col">
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((l, i) => (
            <tr key={i}>
              <th scope="row">{l}</th>
              {series.map((s) => (
                <td key={s.name}>{s.points[i]?.y ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

// ---------- Location scatter ----------

export interface ScatterPoint {
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** Reported accuracy in metres; draws a faint circle. */
  accuracyM?: number;
  kind?: 'person' | 'muster';
}

const EARTH_M = 111_320;

/** Metres east and north of `origin` (flat-earth approximation, fine within a few kilometres). */
export function offsetMetres(origin: { lat: number; lng: number }, p: { lat: number; lng: number }): { east: number; north: number } {
  return { east: (p.lng - origin.lng) * EARTH_M * Math.cos((origin.lat * Math.PI) / 180), north: (p.lat - origin.lat) * EARTH_M };
}

export interface LocationScatterProps {
  title: string;
  /** The site position, drawn in the middle. */
  center: { lat: number; lng: number; name?: string };
  points: readonly ScatterPoint[];
  size?: number;
  /** Smallest half-width shown, in metres. Default 100. */
  minRadiusM?: number;
}

/** Plots points around the site on a plain SVG grid (north is up). No map tiles. A list repeats the positions for screen readers. */
export function LocationScatter({ title, center, points, size = 360, minRadiusM = 100 }: LocationScatterProps) {
  const id = useId();
  const rel = points.map((p) => ({ p, ...offsetMetres(center, p) }));
  const farthest = Math.max(minRadiusM, ...rel.map((r) => Math.max(Math.abs(r.east), Math.abs(r.north))));
  const radius = niceMax(farthest * 1.1);
  const half = size / 2;
  const sx = (m: number) => half + (m / radius) * (half - 16);
  const sy = (m: number) => half - (m / radius) * (half - 16);
  const scalePx = (half - 16) / 2;

  return (
    <figure className="space-y-2">
      <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-labelledby={`${id}-t`} className="h-auto w-full max-w-md rounded-lg border border-slate-200 bg-white">
        <title id={`${id}-t`}>{title}</title>
        <line x1={half} x2={half} y1={8} y2={size - 8} stroke="#e2e8f0" />
        <line x1={8} x2={size - 8} y1={half} y2={half} stroke="#e2e8f0" />
        <circle cx={half} cy={half} r={scalePx} fill="none" stroke="#cbd5e1" strokeDasharray="3 3" />
        <circle cx={half} cy={half} r={half - 16} fill="none" stroke="#cbd5e1" strokeDasharray="3 3" />
        <text x={half + 4} y={half - scalePx - 3} fontSize="10" fill="#475569">
          {t('p3w.scatter.metres', { m: Math.round(radius / 2) })}
        </text>
        <text x={half + 4} y={20} fontSize="10" fill="#475569">
          {t('p3w.scatter.north')}
        </text>
        <rect x={half - 6} y={half - 6} width="12" height="12" fill="#0f172a" />
        <text x={half + 10} y={half + 18} fontSize="11" fill="#0f172a">
          {center.name ?? t('p3w.scatter.site')}
        </text>
        {rel.map(({ p, east, north }) => {
          const acc = p.accuracyM ? Math.min(((p.accuracyM / radius) * (half - 16)), half) : 0;
          const muster = p.kind === 'muster';
          return (
            <g key={p.id}>
              {acc > 3 ? <circle cx={sx(east)} cy={sy(north)} r={acc} fill="#2563eb" fillOpacity="0.1" /> : null}
              {muster ? (
                <polygon points={`${sx(east)},${sy(north) - 7} ${sx(east) + 7},${sy(north) + 6} ${sx(east) - 7},${sy(north) + 6}`} fill="#047857" />
              ) : (
                <circle cx={sx(east)} cy={sy(north)} r="5" fill="#2563eb" stroke="#fff" strokeWidth="1.5" />
              )}
              <title>{p.name}</title>
            </g>
          );
        })}
      </svg>
      <ul className="sr-only" aria-label={title}>
        {rel.map(({ p, east, north }) => (
          <li key={p.id}>{t('p3w.scatter.item', { name: p.name, east: Math.round(east), north: Math.round(north) })}</li>
        ))}
      </ul>
    </figure>
  );
}
